import type { Channel } from '../types';
import { ChannelType } from '../types/enums';

// External channels (social media)
export const externalChannels: Channel[] = [
  {
    id: 'x',
    name: ChannelType.TWITTER,
    displayName: 'X (Twitter)',
    translationKey: 'x',
    color: 'bg-white border border-black',
    textColor: 'text-black',
    enabled: true,
  },
  {
    id: 'facebook',
    name: ChannelType.FACEBOOK,
    displayName: 'Facebook',
    translationKey: 'facebook',
    color: 'bg-blue-600',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'instagram',
    name: ChannelType.INSTAGRAM,
    displayName: 'Instagram',
    translationKey: 'instagram',
    color: 'bg-pink-500',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'linkedin',
    name: ChannelType.LINKEDIN,
    displayName: 'LinkedIn',
    translationKey: 'linkedin',
    color: 'bg-blue-700',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'newsletter',
    name: ChannelType.NEWSLETTER,
    displayName: 'Newsletter',
    translationKey: 'newsletter',
    color: 'bg-purple-600',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'tiktok',
    name: ChannelType.TIKTOK,
    displayName: 'TikTok',
    translationKey: 'tiktok',
    color: 'bg-slate-900',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'youtube',
    name: ChannelType.YOUTUBE,
    displayName: 'YouTube',
    translationKey: 'youtube',
    color: 'bg-red-600',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'pinterest',
    name: ChannelType.PINTEREST,
    displayName: 'Pinterest',
    translationKey: 'pinterest',
    color: 'bg-red-500',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'reddit',
    name: ChannelType.REDDIT,
    displayName: 'Reddit',
    translationKey: 'reddit',
    color: 'bg-orange-600',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'googlebusiness',
    name: ChannelType.GOOGLE_BUSINESS,
    displayName: 'Google Business',
    translationKey: 'googlebusiness',
    color: 'bg-blue-500',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'baidu',
    name: ChannelType.BAIDU,
    displayName: 'Baidu',
    translationKey: 'baidu',
    color: 'bg-blue-800',
    textColor: 'text-white',
    enabled: false, // Désactivé par défaut
  },
  {
    id: 'threads',
    name: ChannelType.THREADS,
    displayName: 'Threads',
    translationKey: 'threads',
    color: 'bg-slate-900',
    textColor: 'text-white',
    enabled: true,
  },
  {
    id: 'bluesky',
    name: ChannelType.BLUESKY,
    displayName: 'Bluesky',
    translationKey: 'bluesky',
    color: 'bg-blue-500',
    textColor: 'text-white',
    enabled: true,
  },
];

// Internal channels (messaging)
export const internalChannels: ({ id: string; name: ChannelType; displayName: string; translationKey: string; color: string; textColor: string } & Partial<Channel>)[] = [
  { id: 'slack', name: ChannelType.SLACK, displayName: 'Slack', translationKey: 'slack', color: 'bg-purple-700', textColor: 'text-white' },
  { id: 'whatsapp', name: ChannelType.WHATSAPP, displayName: 'WhatsApp', translationKey: 'whatsapp', color: 'bg-green-500', textColor: 'text-white' },
  { id: 'telegram', name: ChannelType.TELEGRAM, displayName: 'Telegram', translationKey: 'telegram', color: 'bg-blue-500', textColor: 'text-white' },
  { id: 'teams', name: ChannelType.TEAMS, displayName: 'Microsoft Teams', translationKey: 'teams', color: 'bg-indigo-600', textColor: 'text-white' },
  { id: 'discord', name: ChannelType.DISCORD, displayName: 'Discord', translationKey: 'discord', color: 'bg-indigo-500', textColor: 'text-white' },
];

// Combine all channels
export const defaultChannels: Channel[] = [...externalChannels, ...internalChannels.map(c => ({ ...c, enabled: true } as Channel))];

// Character limits per platform
// IMPORTANT: source of truth is now config/platformConstraints.ts
// This map is kept for backward compatibility with existing UI code.
export const platformCharacterLimits: Record<string, number> = {
  'X': 280,
  'Twitter': 280,
  'twitter': 280,
  'Facebook': 63206,
  'facebook': 63206,
  'Instagram': 2200,
  'instagram': 2200,
  'LinkedIn': 3000,
  'linkedin': 3000,
  'TikTok': 4000,
  'tiktok': 4000,
  'YouTube': 5000,
  'youtube': 5000,
  'Pinterest': 500,
  'pinterest': 500,
  'Newsletter': Infinity,
  'newsletter': Infinity,
  'Slack': 40000,
  'slack': 40000,
  'WhatsApp': 65536,
  'whatsapp': 65536,
  'Telegram': 4096,
  'telegram': 4096,
  'Teams': 28000,
  'teams': 28000,
  'Discord': 2000,
  'discord': 2000,
  'Baidu': 2000,
  'baidu': 2000,
  'Reddit': 40000,
  'reddit': 40000,
  'Google Business': 1500,
  'googlebusiness': 1500,
  'Threads': 500,
  'threads': 500,
  'Bluesky': 300,
  'bluesky': 300,
};

export const getCharacterLimit = (network: string): number => {
  return platformCharacterLimits[network] || Infinity;
};

export const getChannelConfig = (channelName: string): Channel | undefined => {
  return defaultChannels.find(c => c.name === channelName);
};

export const getChannelColor = (channelName: string): string => {
  const channel = getChannelConfig(channelName);
  return channel ? `${channel.color} ${channel.textColor}` : 'bg-gray-500 text-white';
};

/**
 * Plateformes pour lesquelles un carrousel a un sens natif (rendu visuel multi-slide
 * supporté par la plateforme cible). Sur les autres (X, newsletter, Slack…), la zone
 * de génération carrousel n'est pas exposée dans `PostContent` pour éviter la
 * confusion utilisateur.
 *
 * Le set est volontairement restrictif — on étend au cas par cas après validation
 * que la plateforme cible peut publier le carrousel via Zernio.
 */
const CAROUSEL_SUPPORTED_PLATFORMS: ReadonlySet<string> = new Set<string>([
  ChannelType.LINKEDIN,
  ChannelType.INSTAGRAM,
  ChannelType.FACEBOOK,
  // Variantes lower/upper case rencontrées en BDD legacy
  'linkedin',
  'instagram',
  'facebook',
  'LinkedIn',
  'Instagram',
  'Facebook',
]);

/**
 * Retourne true si la plateforme accepte un carrousel (cf {@link CAROUSEL_SUPPORTED_PLATFORMS}).
 * Robuste aux variantes de casse via fallback toLowerCase.
 */
export const isCarouselSupportedPlatform = (platform: string | undefined | null): boolean => {
  if (!platform) return false;
  if (CAROUSEL_SUPPORTED_PLATFORMS.has(platform)) return true;
  return CAROUSEL_SUPPORTED_PLATFORMS.has(platform.toLowerCase());
};

/**
 * Indique si la plateforme cible accepte de publier IMAGE + CARROUSEL sur le
 * même post.
 *
 * À ce jour aucune plateforme native (LinkedIn, Instagram, Facebook, X…) ne
 * supporte cette coexistence : un post = un type de média. La fonction retourne
 * donc toujours `false` pour la v1. Elle est conservée comme point d'extension
 * — si une plateforme l'autorise demain, on n'aura qu'à ajouter son nom au set.
 *
 * Côté UX (chantier Q 2026-05) : si on veut générer un carrousel alors qu'une
 * image existe (ou inverse), le caller doit ouvrir `MediaReplaceConfirmDialog`
 * pour faire valider le remplacement par l'utilisateur.
 */
const COEXIST_IMAGE_AND_CAROUSEL_PLATFORMS: ReadonlySet<string> = new Set<string>([
  // Vide pour l'instant — aucune plateforme native ne supporte la coexistence.
]);

export const canCoexistImageAndCarousel = (platform: string | undefined | null): boolean => {
  if (!platform) return false;
  if (COEXIST_IMAGE_AND_CAROUSEL_PLATFORMS.has(platform)) return true;
  return COEXIST_IMAGE_AND_CAROUSEL_PLATFORMS.has(platform.toLowerCase());
};